import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Input, Popconfirm, Space, Switch, TreeSelect, Typography, App } from 'antd';

import { MAX_BATCH_CASES, batchLimitMessage } from '@/constants/sceneBatch';
import ExistingCompletedModal, { type ExistingCompletedChoice } from './ExistingCompletedModal';
import { findSceneSubtree, perceptionBatchView, perceptionSubmitBody } from './sceneGenerationDomain';
import {
  batchAddScenesToTree,
  cancelSceneBatch,
  checkExistingCompletedScenes,
  createSceneFromCaseFlow,
  getCreateSceneFromCaseFlowStatus,
  getPerceptionSceneTreeRoot,
  getSceneTree,
  resumeSceneBatch,
  type CreateSceneFromCaseFlowState,
  type ExistingCompletedScene,
  type SceneTreeNode,
} from '@/apis/simulation';

const { Text } = Typography;

interface Props {
  canManageSecurity: boolean;
  onFinished?: () => void;
}

/** 感知场景提交卡片。来源、模式、区域都是只读固定值。 */
const PerceptionIngestCard: React.FC<Props> = ({ canManageSecurity, onFinished }) => {
  const { message } = App.useApp();
  const [caseIds, setCaseIds] = useState('');
  const [confidential, setConfidential] = useState(false);
  const [sceneSetNodeId, setSceneSetNodeId] = useState<number | undefined>();
  const [sceneTree, setSceneTree] = useState<SceneTreeNode[]>([]);
  const [rootId, setRootId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState<CreateSceneFromCaseFlowState | null>(null);
  const [acting, setActing] = useState(false);
  const batch = perceptionBatchView(state);
  const [existingPrompt, setExistingPrompt] = useState<{
    items: ExistingCompletedScene[];
    canJoinSet: boolean;
    resolve: (choice: ExistingCompletedChoice) => void;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getSceneTree(false).catch(() => [] as SceneTreeNode[]),
      getPerceptionSceneTreeRoot().catch(() => ''),
    ]).then(([nodes, root]) => {
      if (cancelled) return;
      setSceneTree(nodes ?? []);
      const raw = (root ?? '').trim();
      setRootId(/^[1-9]\d*$/.test(raw) ? Number(raw) : null);
    });
    return () => { cancelled = true; };
  }, []);

  const sceneSetTreeData = useMemo(() => {
    const subtree = findSceneSubtree(sceneTree, rootId);
    const build = (nodes: SceneTreeNode[]): Record<string, unknown>[] =>
      nodes
        .filter(node => !node.isHidden)
        .map(node => {
          const isSet = node.nodeFlag === 1;
          const kids = node.children?.length ? build(node.children) : undefined;
          return {
            title: node.name,
            value: node.id,
            key: node.id,
            selectable: isSet && !node.isLocked,
            disabled: isSet ? !!node.isLocked : false,
            children: kids,
          };
        });
    return build(subtree);
  }, [sceneTree, rootId]);

  const fetchState = useCallback(async () => {
    const st = await getCreateSceneFromCaseFlowStatus('perception');
    setState(st ?? { status: 'idle' });
    return st ?? { status: 'idle' as const };
  }, []);

  useEffect(() => {
    fetchState().catch(() => undefined);
  }, [fetchState]);

  useEffect(() => {
    if (state?.status !== 'running') return;
    const timer = window.setInterval(async () => {
      const next = await fetchState();
      if (next.status === 'running') return;
      if (next.status === 'completed') {
        const failed = next.result?.failed ?? 0;
        if (next.result?.cancelled || next.stage === 'cancelled') {
          message.info('感知入库已取消');
        } else if (failed > 0) {
          message.warning(`感知入库完成，${failed} 条失败`);
        } else {
          message.success('感知入库完成');
        }
        onFinished?.();
      } else if (next.status === 'failed') {
        message.error(next.error || '感知入库失败');
      }
    }, 5000);
    return () => window.clearInterval(timer);
  }, [state?.status, fetchState, message, onFinished]);

  /**
   * 取消 / 继续本批（与规划卡同一组接口，后端按批次的业务域找回感知自己的状态）。
   * 取消 = 放弃本批，剩余条目作废、不可续跑；继续只给「已中断」的批次用。
   */
  const handleBatchAction = async (kind: 'cancel' | 'resume') => {
    if (!batch.jobId) return;
    setActing(true);
    try {
      const res = kind === 'cancel'
        ? await cancelSceneBatch(batch.jobId)
        : await resumeSceneBatch(batch.jobId);
      if (!res.ok) {
        message.error(res.message);
        await fetchState();
        return;
      }
      message.success(res.message);
      if (res.state) setState(res.state as CreateSceneFromCaseFlowState);
      else await fetchState();
      onFinished?.();
    } finally {
      setActing(false);
    }
  };

  const handleSubmit = async () => {
    const inputIds = caseIds.split(/[\s,;;，]+/).map(k => k.trim()).filter(Boolean);
    if (inputIds.length === 0) {
      message.warning('请输入至少一个 Case ID');
      return;
    }
    if (inputIds.length > MAX_BATCH_CASES) {
      message.error(batchLimitMessage(inputIds.length));
      return;
    }
    setSubmitting(true);
    try {
      let items: ExistingCompletedScene[] = [];
      try {
        items = await checkExistingCompletedScenes(inputIds, 'generate_and_qc_check', 'PE');
      } catch {
        return;
      }
      let ids = inputIds;
      if (items.length > 0) {
        const choice = await new Promise<ExistingCompletedChoice>(resolve => {
          setExistingPrompt({ items, canJoinSet: sceneSetNodeId != null, resolve });
        });
        if (choice === 'cancel') return;
        if (choice !== 'regenerate') {
          if (choice === 'ignoreAndJoin' && sceneSetNodeId != null) {
            try {
              await batchAddScenesToTree(sceneSetNodeId, items.map(it => it.sceneId));
            } catch {
              return;
            }
          }
          const done = new Set(items.map(it => it.code));
          ids = inputIds.filter(c => !done.has(c));
          setCaseIds(ids.join('\n'));
          if (ids.length === 0) {
            message.info('剩余 case 均已生成完成，无需提交生成');
            return;
          }
        }
      }
      const resp = await createSceneFromCaseFlow(perceptionSubmitBody(
        ids.join(','),
        sceneSetNodeId,
        canManageSecurity && confidential ? 1 : undefined,
      ));
      if (!resp) return;
      setState(resp.state);
      if (resp.state.status === 'running' && resp.message.includes('运行中')) {
        message.warning(resp.message);
      } else {
        message.success(resp.message || '建场景任务已启动');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card title="感知场景" style={{ flex: '1 1 420px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Text type="secondary">来源 CaseFlow · 模式 事故 · 区域 国内</Text>
        <Input.TextArea
          rows={6}
          placeholder="Case ID，多个用逗号或换行分隔"
          value={caseIds}
          onChange={e => setCaseIds(e.target.value)}
        />
        <TreeSelect
          style={{ width: '100%' }}
          placeholder={rootId == null ? '未配置感知场景子树' : '目标场景集（可选）'}
          treeData={sceneSetTreeData}
          value={sceneSetNodeId}
          allowClear
          disabled={rootId == null}
          treeDefaultExpandAll
          onChange={value => setSceneSetNodeId(value as number | undefined)}
        />
        {canManageSecurity && (
          <div>
            <Switch checked={confidential} onChange={setConfidential} />
            <Text style={{ marginLeft: 8 }}>保密</Text>
          </div>
        )}
        <Space wrap>
          <Button
            type="primary"
            loading={submitting}
            disabled={batch.running}
            onClick={handleSubmit}
          >
            {batch.interrupted ? '批次未结束' : '提交入库'}
          </Button>
          {batch.interrupted && (
            <Button loading={acting} disabled={!batch.jobId} onClick={() => handleBatchAction('resume')}>
              继续
            </Button>
          )}
          {/* 中断的批次也要能取消：同一用户同时只能跑一批，否则会被卡在跑不完的批次上。 */}
          {batch.running && batch.jobId && (
            <Popconfirm
              title="取消本批？"
              description={batch.interrupted
                ? '本批已中断，取消后剩余条目作废，不可续跑。'
                : '未处理的条目将置为取消，本批不可续跑。'}
              okText="取消本批"
              okButtonProps={{ danger: true }}
              cancelText="再想想"
              onConfirm={() => handleBatchAction('cancel')}
            >
              <Button danger loading={acting}>取消本批</Button>
            </Popconfirm>
          )}
        </Space>
        {batch.running && (
          <Text type={batch.interrupted ? 'warning' : 'secondary'}>
            {batch.progressText}
            {batch.interrupted && state?.error ? `；原因：${state.error}` : ''}
          </Text>
        )}
      </div>
      <ExistingCompletedModal
        items={existingPrompt?.items ?? []}
        canJoinSet={existingPrompt?.canJoinSet ?? false}
        modeLabel="事故"
        onChoose={choice => {
          existingPrompt?.resolve(choice);
          setExistingPrompt(null);
        }}
      />
    </Card>
  );
};

export default PerceptionIngestCard;
